import React from 'react';
import { useForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { FieldTextInput } from '../../components';

import css from './FieldRichTextarea.module.css';

const BOLD = '**';
const ITALIC = '*';
const UNDERLINE = '__';

// Asterisks next to each other: 1 = italic, 2 = bold, 3 = bold italic. Underscores: 2 = underline
const hasMarker = (chars, marker) =>
  marker === ITALIC ? Math.min(chars, 3) % 2 === 1 : chars >= 2;

const runAtStart = (str, char) => {
  let n = 0;
  while (str[n] === char) n++;
  return n;
};
const runAtEnd = (str, char) => {
  let n = 0;
  while (n < str.length && str[str.length - 1 - n] === char) n++;
  return n;
};

/**
 * Toggle bold (**), italic (*) or underline (__) markers around the selected range of the given text.
 * Without a selection, empty markers are added and the caret is placed between them.
 *
 * @param {string} value current text
 * @param {number} start selection start
 * @param {number} end selection end
 * @param {string} marker '**' (default), '*' or '__'
 * @returns {Object} { value, selectionStart, selectionEnd }
 */
export const toggleMarker = (value, start, end, marker = BOLD) => {
  const len = marker.length;
  const char = marker[0];
  // Asterisk and underscore markers can be nested: look for the marker behind the other kind too
  const otherChar = char === '*' ? '_' : '*';
  const before = value.slice(0, start);
  const selected = value.slice(start, end);
  const after = value.slice(end);

  // Selection is surrounded by markers: "**|text|**"
  const outerBefore = before.slice(0, before.length - runAtEnd(before, otherChar));
  const outerAfter = after.slice(runAtStart(after, otherChar));
  if (hasMarker(Math.min(runAtEnd(outerBefore, char), runAtStart(outerAfter, char)), marker)) {
    return {
      value: [
        outerBefore.slice(0, -len),
        before.slice(outerBefore.length),
        selected,
        after.slice(0, after.length - outerAfter.length),
        outerAfter.slice(len),
      ].join(''),
      selectionStart: start - len,
      selectionEnd: end - len,
    };
  }
  // Selection includes markers: "|**text**|"
  const innerStart = runAtStart(selected, otherChar);
  const innerEnd = selected.length - runAtEnd(selected, otherChar);
  const inner = selected.slice(innerStart, innerEnd);
  const charsInside = Math.min(runAtStart(inner, char), runAtEnd(inner, char));
  if (inner.length > 2 * charsInside && hasMarker(charsInside, marker)) {
    const unwrapped = [
      selected.slice(0, innerStart),
      inner.slice(len, -len),
      selected.slice(innerEnd),
    ].join('');
    return {
      value: `${before}${unwrapped}${after}`,
      selectionStart: start,
      selectionEnd: start + unwrapped.length,
    };
  }

  // Markers don't work with surrounding whitespace: keep it outside of them
  const leading = selected.match(/^\s*/)[0].length;
  const trailing = selected.length > leading ? selected.match(/\s*$/)[0].length : 0;
  const core = selected.slice(leading, selected.length - trailing);
  const selectionStart = start + leading + len;
  return {
    value: `${before}${selected.slice(0, leading)}${marker}${core}${marker}${selected.slice(
      selected.length - trailing
    )}${after}`,
    selectionStart,
    selectionEnd: selectionStart + core.length,
  };
};

/**
 * Final Form textarea field with "Bold", "Italic" and "Underline" buttons (and Cmd/Ctrl+B/I/U shortcuts).
 * Phrases are saved as **bold text**, *italic text* and __underlined text__, and they can be rendered
 * with richText util using the "bold" option.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string} props.id id of the textarea (required)
 * @param {string} props.name Name of the input in Final Form
 * @returns {JSX.Element} FieldTextInput (textarea) with bold button
 */
const FieldRichTextarea = props => {
  const { className, id, name, ...rest } = props;
  const form = useForm();
  const intl = useIntl();

  const applyMarker = marker => {
    const textarea = document.getElementById(id);
    if (!textarea) {
      return;
    }
    const { value, selectionStart, selectionEnd } = toggleMarker(
      textarea.value || '',
      textarea.selectionStart,
      textarea.selectionEnd,
      marker
    );
    form.change(name, value);

    // Restore the selection after the new value has been rendered
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const handleKeyDown = e => {
    const marker = { b: BOLD, i: ITALIC, u: UNDERLINE }[e.key.toLowerCase()];
    if ((e.metaKey || e.ctrlKey) && !e.altKey && marker) {
      e.preventDefault();
      applyMarker(marker);
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      <FieldTextInput id={id} name={name} type="textarea" onKeyDown={handleKeyDown} {...rest} />
      <div className={css.toolbar}>
        <button
          type="button"
          className={css.boldButton}
          // Keep the focus (and selection) in the textarea
          onMouseDown={e => e.preventDefault()}
          onClick={() => applyMarker(BOLD)}
          title={intl.formatMessage({ id: 'FieldRichTextarea.boldButtonTitle' })}
          aria-label={intl.formatMessage({ id: 'FieldRichTextarea.boldButtonTitle' })}
        >
          B
        </button>
        <button
          type="button"
          className={css.italicButton}
          onMouseDown={e => e.preventDefault()}
          onClick={() => applyMarker(ITALIC)}
          title={intl.formatMessage({ id: 'FieldRichTextarea.italicButtonTitle' })}
          aria-label={intl.formatMessage({ id: 'FieldRichTextarea.italicButtonTitle' })}
        >
          I
        </button>
        <button
          type="button"
          className={css.underlineButton}
          onMouseDown={e => e.preventDefault()}
          onClick={() => applyMarker(UNDERLINE)}
          title={intl.formatMessage({ id: 'FieldRichTextarea.underlineButtonTitle' })}
          aria-label={intl.formatMessage({ id: 'FieldRichTextarea.underlineButtonTitle' })}
        >
          U
        </button>
        <span className={css.hint}>
          <FormattedMessage id="FieldRichTextarea.hint" />
        </span>
      </div>
    </div>
  );
};

export default FieldRichTextarea;
